// src/templateManager/hooks/useAutoSave.js
import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * useAutoSave hook - Automatically saves form data at regular intervals
 *
 * @param {Function} saveFn - Function to call for saving (receives form data)
 * @param {Object} formData - Current form data to save
 * @param {Object} options - Configuration options
 * @param {number} options.interval - Save interval in milliseconds (default: 30000 = 30s)
 * @param {boolean} options.enabled - Whether auto-save is enabled (default: true)
 * @param {Function} options.shouldSave - Function to determine if save should happen (optional)
 *
 * @returns {Object} - { isSaving, lastSaved, forceSave }
 */
export function useAutoSave(saveFn, formData, options = {}) {
  const {
    interval = 30000, // 30 seconds default
    enabled = true,
    shouldSave = () => true
  } = options;

  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const timeoutRef = useRef(null);
  const lastDataRef = useRef(formData);
  const isMountedRef = useRef(true);

  // Save function
  const save = useCallback(async () => {
    if (!enabled || !shouldSave(formData)) {
      return;
    }

    // Check if data actually changed
    if (JSON.stringify(formData) === JSON.stringify(lastDataRef.current)) {
      console.log('[useAutoSave] No changes detected, skipping save');
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      console.log('[useAutoSave] Auto-saving...');
      await saveFn(formData);

      if (isMountedRef.current) {
        setLastSaved(new Date());
        lastDataRef.current = formData;
        console.log('[useAutoSave] Auto-save successful');
      }
    } catch (error) {
      console.error('[useAutoSave] Save error:', error);
      if (isMountedRef.current) {
        setSaveError(error.message);
      }
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [saveFn, formData, enabled, shouldSave]);

  // Force save (manual trigger)
  const forceSave = useCallback(async () => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    await save();

    // Restart auto-save timer
    if (enabled) {
      timeoutRef.current = setTimeout(save, interval);
    }
  }, [save, enabled, interval]);

  // Auto-save effect
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Set up auto-save interval
    timeoutRef.current = setTimeout(save, interval);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [save, interval, enabled, formData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    isSaving,
    lastSaved,
    saveError,
    forceSave
  };
}

export default useAutoSave;
